/**
 * Lernerprofil for the lecturer (learner-profile v2): the contract of
 * al-tutor `learner_memory_ops.memory_view` (audience "lecturer") and the
 * pure helpers that turn it into rows. Tested in learner-memory.spec.ts.
 */

export type ObservationType = 'observation' | 'milestone' | 'preference' | 'correction';
export const TYPE_ORDER: ObservationType[] = ['milestone', 'observation', 'correction', 'preference'];
export const KNOWN_PREFERENCES = ['favorite_color', 'preferred_pace', 'preferred_format', 'language_pref', 'hobby', 'timezone'];
export const KNOWLEDGE_CODES = ['beginner', 'intermediate', 'advanced', 'professional'];
export const I18N = 'components.learnerMemory';
/** Status-chip tone per kind (global `.al-status-chip--*`). */
export const TONE: Record<ObservationType, string> = {
  milestone: 'accept', observation: 'info', correction: 'warning', preference: 'neutral',
};

export interface MemoryObservation {
  ref: string | null;
  type: ObservationType;
  content: string;
  subtopic: string | null;
  source: 'chat' | 'exercise' | null;
  agent: string | null;
  course: string | null;
  created_at: string | null;
  confidence?: number;
}

export interface MemoryGroup {
  label: string | null;
  count: number;
  last_at: string | null;
  types: Record<ObservationType, number>;
  items: MemoryObservation[];
}

export interface MemoryView {
  version: number;
  audience: 'lecturer' | 'learner';
  summary: { text: string | null; learner_text?: string | null; updated_at: string | null };
  facts: { field: string; value: string; code?: string }[];
  preferences: { key: string; label: string | null; value: string }[];
  topics: { strong: { raw: string; label: string }[]; weak: { raw: string; label: string }[] };
  observations: {
    total: number;
    by_type: Record<ObservationType, number>;
    groups: MemoryGroup[];
    weekly: ({ week_start: string } & Record<ObservationType, number>)[];
  };
  exercise_topics: ExerciseTopic[];
  figures: {
    interactions: number; observations: number; milestones: number; exercises: number;
    exercises_correct: number; streak_days: number; last_activity: string | null;
  };
  memory: { available: boolean; enabled: boolean };
  empty: boolean;
}

export interface ExerciseTopic {
  label: string;
  /** mastery_core tier from the ledger (mastery-objectives M1); older servers send only `level` */
  tier?: string;
  level: string | null;
  done: number;
  correct: number;
  last_practiced: string | null;
  weak: string[];
  score?: number;
  source?: 'ledger' | 'store';
}

export type Translate = (key: string, params?: Record<string, unknown>) => string;

export function humanizeKey(key: string): string {
  const words = String(key || '').replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

export function preferenceLabel(p: { key: string; label: string | null }, t: Translate): string {
  if (p.label) return p.label;
  if (KNOWN_PREFERENCES.includes(p.key)) return t(`${I18N}.pref.${p.key}`);
  return humanizeKey(p.key);
}

export function factValue(f: { field: string; value: string; code?: string }, t: Translate): string {
  if (f.field === 'knowledge_level' && f.code && KNOWLEDGE_CODES.includes(f.code)) return t(`${I18N}.level.${f.code}`);
  return f.value;
}

export function shortDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(lang || 'de', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export interface ObservationRow {
  key: string;
  type: ObservationType;
  tone: string;
  typeLabel: string;
  text: string;
  meta: string;
}

export interface GroupRow {
  key: string;
  label: string;
  count: number;
  chips: { type: ObservationType; tone: string; text: string }[];
  items: ObservationRow[];
}

/** Groups filtered by type and free text; new arrays only when the filter
 *  or the data change (the component memoises — Regel 18). */
export function filterGroups(
  groups: MemoryGroup[], type: ObservationType | 'all', query: string, t: Translate, lang: string,
): GroupRow[] {
  const q = query.trim().toLowerCase();
  const out: GroupRow[] = [];
  groups.forEach((g, gi) => {
    const label = g.label || t(`${I18N}.general`);
    const items = g.items
      .filter((o) => type === 'all' || o.type === type)
      .filter((o) => !q || [o.content, o.subtopic, label, o.course, o.agent]
        .some((v) => (v || '').toLowerCase().includes(q)))
      .map((o, i) => ({
        key: o.ref || `${gi}-${i}`,
        type: o.type,
        tone: TONE[o.type] || 'info',
        typeLabel: t(`${I18N}.type.${o.type}`),
        text: o.type === 'correction' ? t(`${I18N}.correctedPrefix`, { text: o.content }) : o.content,
        meta: [
          o.subtopic,
          o.source ? t(`${I18N}.source.${o.source}`) : null,
          o.course,
          o.agent,
          typeof o.confidence === 'number' ? t(`${I18N}.confidence`, { value: Math.round(o.confidence * 100) }) : null,
          shortDate(o.created_at, lang),
        ].filter(Boolean).join(' · '),
      }));
    if (!items.length) return;
    out.push({
      key: `${label}-${gi}`,
      label,
      count: items.length,
      chips: TYPE_ORDER.filter((k) => g.types?.[k]).map((k) => ({ type: k, tone: TONE[k], text: `${g.types[k]} ${t(`${I18N}.type.${k}`)}` })),
      items,
    });
  });
  return out;
}


// ─── Charts (mastery-objectives M1) ─────────────────────────────────────────

/** Mastery tiers in order (mastery_core.TIERS); `new` reads "Offen". */
export const TIERS = ['new', 'learning', 'proficient', 'mastered', 'verified'] as const;
export type Tier = (typeof TIERS)[number];
/** Tier colours: open in the track grey, then the chart palette, verified in accept green. */
export const TIER_COLOR: Record<Tier, string> = {
  new: '#dfe6f0', learning: '#93c5fd', proficient: '#3b82f6', mastered: '#1d4ed8', verified: '#00a35c',
};
/** Observation kinds in the bars (chart palette; correction amber like the chip). */
export const TYPE_COLOR: Record<ObservationType, string> = {
  milestone: '#007BFF', observation: '#004DB7', correction: '#e39b1b', preference: '#9fb0cc',
};
const LEVEL_TIER: Record<string, Tier> = {
  novice: 'learning', developing: 'learning', proficient: 'proficient', mastered: 'mastered',
};

export function tierOf(e: ExerciseTopic): Tier {
  if (e.tier && (TIERS as readonly string[]).includes(e.tier)) return e.tier as Tier;
  return (e.level && LEVEL_TIER[e.level]) || 'learning';
}

/** 0..4 filled dots for a tier (Im Aufbau 1 … Bestätigt 4). */
export function tierDots(tier: Tier): number {
  return Math.max(0, TIERS.indexOf(tier));
}

export interface TopicBar {
  key: string;
  label: string;
  tier: Tier;
  tierLabel: string;
  dots: boolean[];
  color: string;
  /** 0..100 */
  pct: number;
  meta: string;
  weak: string[];
}

/** Exercise topics as bars, best result first. */
export function topicBars(topics: ExerciseTopic[], t: Translate): TopicBar[] {
  return topics
    .map((e, i) => {
      const tier = tierOf(e);
      const dots = tierDots(tier);
      return {
        key: `${i}-${e.label}`,
        label: e.label,
        tier,
        tierLabel: t(`${I18N}.tier.${tier}`),
        dots: [1, 2, 3, 4].map((d) => d <= dots),
        color: TIER_COLOR[tier === 'new' ? 'learning' : tier],
        pct: e.done ? Math.round((100 * e.correct) / e.done) : 0,
        // No answers counted (a level the agent set, #227): no "0 von 0".
        meta: e.done ? t(`${I18N}.ex.correctValue`, { correct: e.correct, total: e.done }) : '',
        weak: e.weak,
      };
    })
    .sort((a, b) => b.pct - a.pct || a.label.localeCompare(b.label));
}

export interface StackSegment {
  key: string;
  color: string;
  /** flex weight */
  n: number;
  title: string;
}

export interface MasteryRow {
  key: string;
  label: string;
  segments: StackSegment[];
  /** bar length relative to the largest area, 0..100 */
  width: number;
  count: string;
  aria: string;
}

/** One stacked bar per area: its objectives by tier. */
export function masteryRows(
  axes: { key: string; label: string | null; total: number; tiers: Record<string, number> }[], t: Translate,
): MasteryRow[] {
  const max = Math.max(1, ...axes.map((a) => a.total));
  return axes
    .filter((a) => a.total > 0)
    .map((a) => {
      const segments = TIERS.filter((tier) => a.tiers?.[tier]).map((tier) => ({
        key: tier, color: TIER_COLOR[tier], n: a.tiers[tier], title: `${a.tiers[tier]} ${t(`${I18N}.tier.${tier}`)}`,
      }));
      const sure = (a.tiers?.['proficient'] || 0) + (a.tiers?.['mastered'] || 0) + (a.tiers?.['verified'] || 0);
      const label = a.label || '—';
      return {
        key: a.key,
        label,
        segments,
        width: Math.round((100 * a.total) / max),
        count: `${sure}/${a.total}`,
        aria: `${label}: ${segments.map((s) => s.title).join(', ')}`,
      };
    });
}

/** SOLO levels (Biggs), lowest first — al-tutor `competency_profile_ops.SOLO_LEVELS` (PRD mastery-objectives M5). */
export const SOLO_LEVELS = ['prestructural', 'unistructural', 'multistructural', 'relational', 'extended_abstract'] as const;
export type SoloLevel = (typeof SOLO_LEVELS)[number];
/** One sequential blue ramp, light → dark: the deeper the answer, the darker. */
export const SOLO_COLOR: Record<SoloLevel, string> = {
  prestructural: '#dfe6f0', unistructural: '#93c5fd', multistructural: '#3b82f6', relational: '#1d4ed8',
  extended_abstract: '#01154f',
};

export interface SoloBar {
  total: number;
  /** only levels with answers — the bar */
  segments: StackSegment[];
  /** every level with its count — the legend */
  legend: { key: SoloLevel; color: string; label: string; n: number }[];
  aria: string;
}

/** Typed answers per SOLO level as one stacked bar; null without any. */
export function soloBar(solo: Partial<Record<string, number>> | null | undefined, t: Translate): SoloBar | null {
  const legend = SOLO_LEVELS.map((level) => ({
    key: level, color: SOLO_COLOR[level], label: t(`${I18N}.solo.level.${level}`), n: Math.max(0, solo?.[level] || 0),
  }));
  const total = legend.reduce((sum, l) => sum + l.n, 0);
  if (!total) return null;
  const segments = legend.filter((l) => l.n).map((l) => ({ key: l.key, color: l.color, n: l.n, title: `${l.n} ${l.label}` }));
  return {
    total,
    segments,
    legend,
    aria: `${t(`${I18N}.solo.title`)}: ${segments.map((s) => s.title).join(', ')}`,
  };
}

export interface ObservationBar extends GroupRow {
  segments: StackSegment[];
  width: number;
}

/** Observation groups as stacked bars (kinds), longest first — the items open on click. */
export function observationBars(groups: GroupRow[], source: MemoryGroup[]): ObservationBar[] {
  const max = Math.max(1, ...groups.map((g) => g.count));
  return groups.map((g) => {
    const counts = new Map<ObservationType, number>();
    g.items.forEach((o) => counts.set(o.type, (counts.get(o.type) || 0) + 1));
    return {
      ...g,
      segments: TYPE_ORDER.filter((k) => counts.get(k)).map((k) => ({
        key: k, color: TYPE_COLOR[k], n: counts.get(k)!, title: g.chips.find((c) => c.type === k)?.text || k,
      })),
      width: Math.round((100 * g.count) / max),
    };
  });
}

/** Index of the knowledge level on the four-step scale, or -1. */
export function knowledgeStep(facts: { field: string; code?: string }[]): number {
  const f = facts.find((x) => x.field === 'knowledge_level');
  return f?.code ? KNOWLEDGE_CODES.indexOf(f.code) : -1;
}

/** stroke-dasharray / -offset for a ring of radius r filled to pct (0..1). */
export function ringDash(pct: number, r: number): { array: string; offset: number } {
  const c = 2 * Math.PI * r;
  return { array: `${c.toFixed(2)}`, offset: +(c * (1 - Math.max(0, Math.min(1, pct)))).toFixed(2) };
}

/** Points of a sparkline over `values` in a w×h box. */
export function sparkPoints(values: number[], w: number, h: number): string {
  if (values.length < 2) return '';
  const max = Math.max(1, ...values);
  return values.map((v, i) => `${((i * w) / (values.length - 1)).toFixed(1)},${(h - 3 - (v / max) * (h - 6)).toFixed(1)}`).join(' ');
}
